/**
 * Arkanoid C64 Constants and Configurations (16:9 Widescreen Edition)
 */

// Authentic Commodore 64 16-Color Palette in Normalized RGB ([0..1]) for WebGL Fragment Shaders
export const C64_PALETTE_GL = [
  [0.0, 0.0, 0.0],          // 0: Black
  [1.0, 1.0, 1.0],          // 1: White
  [0.533, 0.0, 0.0],        // 2: Red
  [0.667, 1.0, 0.933],      // 3: Cyan
  [0.8, 0.267, 0.8],        // 4: Purple
  [0.0, 0.8, 0.333],        // 5: Green
  [0.0, 0.0, 0.667],        // 6: Blue
  [0.933, 0.933, 0.467],    // 7: Yellow
  [0.867, 0.533, 0.333],    // 8: Orange
  [0.4, 0.267, 0.0],        // 9: Brown
  [1.0, 0.467, 0.467],      // 10: Light Red
  [0.2, 0.2, 0.2],          // 11: Dark Grey
  [0.467, 0.467, 0.467],    // 12: Grey
  [0.667, 1.0, 0.4],        // 13: Light Green
  [0.0, 0.533, 1.0],        // 14: Light Blue
  [0.733, 0.733, 0.733],    // 15: Light Grey
];

// Hex strings for UI overlay
export const C64_HEX = [
  '#000000', '#ffffff', '#880000', '#aaffee',
  '#cc44cc', '#00cc55', '#0000aa', '#eeee77',
  '#dd8855', '#664400', '#ff7777', '#333333',
  '#777777', '#aaff66', '#0088ff', '#bbbbbb',
];

// 16:9 Widescreen Arcade Resolution
export const VIRTUAL_WIDTH = 960;
export const VIRTUAL_HEIGHT = 540;

export const WALL_WIDTH = 32;

// Playing field boundary inside the solid black unbreakable side walls
export const PLAYFIELD = {
  left: WALL_WIDTH,                   // 32
  right: VIRTUAL_WIDTH - WALL_WIDTH,  // 928
  top: 40,
  bottom: 535,
  width: VIRTUAL_WIDTH - WALL_WIDTH * 2, // 896
  height: 495,
};

// Brick grid dimensions (15 columns x 14 rows widescreen matrix)
export const GRID_COLS = 15;
export const GRID_ROWS = 14;
export const BRICK_WIDTH = PLAYFIELD.width / GRID_COLS; // ~59.73 pixels
export const BRICK_HEIGHT = 18;
export const BRICK_START_Y = PLAYFIELD.top + 20;

// Brick Types & Attributes
export const BRICK_TYPES = {
  EMPTY: 0,
  WHITE: 1,
  ORANGE: 2,
  CYAN: 3,
  GREEN: 4,
  RED: 5,
  BLUE: 6,
  PURPLE: 7,
  YELLOW: 8,
  SILVER: 9,
  GOLD: 10,
  BLACK_WALL: 11,
};

export const BRICK_DATA = {
  [BRICK_TYPES.WHITE]:      { colorIndex: 1,  points: 50,  hits: 1 },
  [BRICK_TYPES.ORANGE]:     { colorIndex: 8,  points: 60,  hits: 1 },
  [BRICK_TYPES.CYAN]:       { colorIndex: 3,  points: 70,  hits: 1 },
  [BRICK_TYPES.GREEN]:      { colorIndex: 5,  points: 80,  hits: 1 },
  [BRICK_TYPES.RED]:        { colorIndex: 2,  points: 90,  hits: 1 },
  [BRICK_TYPES.BLUE]:       { colorIndex: 6,  points: 100, hits: 1 },
  [BRICK_TYPES.PURPLE]:     { colorIndex: 4,  points: 110, hits: 1 },
  [BRICK_TYPES.YELLOW]:     { colorIndex: 7,  points: 120, hits: 1 },
  [BRICK_TYPES.SILVER]:     { colorIndex: 15, points: 150, hits: 2, silver: true },
  [BRICK_TYPES.GOLD]:       { colorIndex: 7,  points: 0,   hits: -1, unbreakable: true },
  [BRICK_TYPES.BLACK_WALL]: { colorIndex: 0,  points: 0,   hits: -1, unbreakable: true },
};

// Collectible Powerup Capsules
export const COLLECTIBLE_TYPES = {
  SLOW: 'S',      // Decelerates ball
  CATCH: 'C',     // Sticky paddle
  LASER: 'L',     // Laser blasters
  EXPAND: 'E',    // Wider paddle
  DISRUPT: 'D',   // Splits into 3 balls
  BREAK: 'B',     // Open portal / warp
  PLAYER: 'P',    // 1UP Extra Life
};

export const COLLECTIBLE_COLORS = {
  [COLLECTIBLE_TYPES.SLOW]: 8,     // Orange
  [COLLECTIBLE_TYPES.CATCH]: 5,    // Green
  [COLLECTIBLE_TYPES.LASER]: 2,    // Red
  [COLLECTIBLE_TYPES.EXPAND]: 14,  // Light Blue
  [COLLECTIBLE_TYPES.DISRUPT]: 3,  // Cyan
  [COLLECTIBLE_TYPES.BREAK]: 4,    // Purple
  [COLLECTIBLE_TYPES.PLAYER]: 12,  // Grey
};

// Difficulty Presets
export const DIFFICULTIES = {
  EASY: {
    id: 'EASY',
    name: 'CADET (EASY)',
    initialLives: 5,
    paddleWidth: 120,
    ballSpeed: 4.8,
    speedIncrement: 0.04,
    maxSpeed: 8.5,
    dropRate: 0.32,
    silverHits: 2,
  },
  NORMAL: {
    id: 'NORMAL',
    name: 'PILOT (NORMAL)',
    initialLives: 3,
    paddleWidth: 96,
    ballSpeed: 5.6,
    speedIncrement: 0.07,
    maxSpeed: 10.5,
    dropRate: 0.22,
    silverHits: 3,
  },
  HARD: {
    id: 'HARD',
    name: 'MASTER (HARD)',
    initialLives: 2,
    paddleWidth: 78,
    ballSpeed: 6.8,
    speedIncrement: 0.11,
    maxSpeed: 12.5,
    dropRate: 0.14,
    silverHits: 4,
  },
};

// Game States
export const GAME_STATES = {
  MENU: 'MENU',
  SETTINGS: 'SETTINGS',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  LEVEL_CLEAR: 'LEVEL_CLEAR',
  GAME_OVER: 'GAME_OVER',
  VICTORY: 'VICTORY',
};

// Shader Limits (passed to GLSL uniforms)
export const MAX_BALLS = 8;
export const MAX_CAPSULES = 8;
export const MAX_LASERS = 12;
export const MAX_PARTICLES = 32;
